<template>
    <div class="d-flex flex-wrap flex-stack my-5">
        <h2 class="fs-2 fw-bold my-2">
            {{ sprintf($t('pages.ecommerce.invoiceManagement.provider.paymentMethod.titlePattern'), [provider.name ?? '']) }}
        </h2>

        <router-link to="/ecommerce/invoice-management/provider" class="btn btn-primary align-self-center">
            <span class="svg-icon svg-icon-2">
                <inline-svg src="/media/icons/duotune/arrows/arr021.svg"/>
            </span>
            {{ $t("pages.ecommerce.invoiceManagement.provider.title") }}
        </router-link>
    </div>
    <custom-table
        :title="$t('pages.ecommerce.invoiceManagement.provider.paymentMethod.title')"
        :subTitle="$t('pages.ecommerce.invoiceManagement.provider.paymentMethod.subTitle')"
        selectableRows
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        :columns="fields"
        :actions="actions"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow">
        <template v-slot:paymentSubMethod="{ row: record }">
            {{ record.payment_sub_method ? record.payment_sub_method.payload.name : '-' }}
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <a v-on:click="fetchPaymentMethod(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                    </span>
                </a>
                <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                    <template #reference>
                        <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                            </span>
                        </a>
                    </template>
                </el-popconfirm>
            </div>
        </template>
    </custom-table>
    <div class="modal fade" id="kt_modal_add_payment_method" ref="addPaymentMethodModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-650px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div id="kt_modal_add_customer_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1">
                            <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                        </span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmit()" :model="form.data" ref="paymentMethodForm">
                    <div class="modal-body py-10 px-lg-17">
                        <div class="scroll-y me-n7 pe-7" id="kt_modal_add_customer_scroll" data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto" data-kt-scroll-dependencies="#kt_modal_add_customer_header" data-kt-scroll-wrappers="#kt_modal_add_customer_scroll" data-kt-scroll-offset="300px">
                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.paymentMethod') }}</label>
                                <el-form-item prop="payment_method_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.payment_method_id" class="w-100" :placeholder="$t('common.chooseSelect')" @change="selectedPaymentMethod" :disabled="form.updateStatus">
                                        <el-option v-for="(paymentMethod, paymentMethodIndex) in paymentMethods" :key="paymentMethodIndex" :value="paymentMethod.id" :label="paymentMethod.name">{{ paymentMethod.name }}</el-option>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7" v-if="paymentSubMethods.length">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.paymentSubMethod') }}</label>
                                <el-form-item prop="payment_sub_method_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.payment_sub_method_id" class="w-100" :placeholder="$t('common.chooseSelect')" :disabled="form.updateStatus">
                                        <el-option v-for="(paymentSubMethod, paymentSubMethodIndex) in paymentSubMethods" :key="paymentSubMethodIndex" :value="paymentSubMethod.id" :label="paymentSubMethod.payload.name"></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.providerAccountID') }}</label>
                                <el-form-item prop="provider_account_id" :rules="$validation.getMessage(['required'])">
                                    <el-input v-model="form.data.provider_account_id" type="text"/>
                                </el-form-item>
                            </div>

                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "_id",
    components: {
        CustomTable
    },
    data() {
        return {
            fields: [
                {
                    name: this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.id"),
                    key: "id"
                },
                {
                    name: this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.paymentMethod"),
                    key: "payment_method.name"
                },
                {
                    name: this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.paymentSubMethod"),
                    scopedSlots: {customRender: "paymentSubMethod"}
                },
                {
                    name: this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.cols.providerAccountID"),
                    key: "provider_account_id"
                },
                {
                    name: this.$t("common.action"),
                    key: "action",
                    scopedSlots: {customRender: "actions"}
                }
            ],
            actions: [
                {
                    name: "new",
                    icon: "bi-plus-lg",
                    label: "btn.add"
                },
                {
                    name: "refresh",
                    icon: "bi-arrow-repeat",
                    label: "btn.refresh"
                },
                {
                    name: "delete",
                    icon: "bi-trash",
                    label: "btn.delete"
                }
            ],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    payload: {},
                }
            },
            selectedRowKeys: [],
            table: {
                data: [],
                loading: false
            },
            provider: {},
            paymentSubMethods: []
        }
    },
    computed: {
        paymentMethods() {
            return this.$store.state.ecommerce.paymentMethod.table.data;
        },
        providerID() {
            return this.$route.params.id;
        }
    },
    created() {
        if (!this.providerID || !(this.providerID > 0)) {
            this.$router.push({
                path: "/ecommerce/invoice-management/provider"
            });
        }
    },
    mounted() {
        if (this.providerID && this.providerID > 0) {
            this.loadProvider();
            this.$store.dispatch('ecommerce/paymentMethod/get', {
                page: { pageSize: 9999 }
            });
        }
    },
    methods: {
        loadProvider() {
            this.table.loading = true;

            this.axios.get(this.endpoints['ecommerce_invoice_provider'] + '/' + this.providerID).then((response) => {
                let data = response.data.data;
                this.provider = data;
                this.table.data = data.payment_method_relations;
            }).finally(() => {
                this.table.loading = false;
            });
        },
        newPaymentMethod() {
            this.form.updateStatus = false;
            this.form.data = {
                provider_id: this.providerID
            };
            this.form.title = this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.newPaymentMethod");
            this.showModal(this.$refs.addPaymentMethodModal);
        },
        fetchPaymentMethod(record) {
            this.form.updateStatus = true;
            this.form.title = this.$t("pages.ecommerce.invoiceManagement.provider.paymentMethod.editPaymentMethod");
            this.axios.get(this.endpoints['ecommerce_invoice_provider_payment_method'] + '/' + record.id).then(response => {
                let data = response.data.data;
                this.form.data = data;
                this.selectedPaymentMethod(false);
                this.showModal(this.$refs.addPaymentMethodModal);
            });
        },
        onSubmit() {
            this.$refs.paymentMethodForm.validate((valid) => {
                if (valid) {
                    this.form.loading = true;

                    if (this.form.data.id) {
                        this.axios.put(this.endpoints['ecommerce_invoice_provider_payment_method'] + '/' + this.form.data.id, this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadProvider();
                                this.hideModal(this.$refs.addPaymentMethodModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    } else {
                        this.axios.post(this.endpoints['ecommerce_invoice_provider_payment_method'], this.form.data).then(response => {
                            this.onResponse(response.data, () => {
                                this.loadProvider();
                                this.hideModal(this.$refs.addPaymentMethodModal);
                            }, () => {
                                this.form.loading = false;
                            });
                        }).catch(error => {
                            this.onResponseFailure(error.response.data, () => {
                                this.form.loading = false;
                            });
                        });
                    }
                } else {
                    return false;
                }
            });
        },
        deleteRecord(id) {
            this.$store.dispatch("ecommerce/invoiceManagement/provider/paymentMethod/delete", {
                id: id
            }).then((successDeleted) => {
                this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                if (successDeleted) {
                    this.loadProvider();
                }
            });
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.loadProvider();
                    break;

                case "new":
                    this.newPaymentMethod();
                    break;

                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;

                default:
                    break;
            }
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        selectedPaymentMethod(resetPaymentSubMethod = true) {
            if(resetPaymentSubMethod){
                this.form.data.payment_sub_method_id = null;
            }

            if (this.form.data.payment_method_id) {
                this.axios.get(this.endpoints['ecommerce_payment_method'] + '/' + this.form.data.payment_method_id).then((response) => {
                    let data = response.data.data;
                    this.paymentSubMethods = data.sub_methods;
                })
            }
        },
    }
}
</script>

<style scoped>

</style>